import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../layouts/DashboardLayout';
import FollowButton from '../../components/social/FollowButton';
import {
  PublicProfile,
  SocialSummary,
  getPublicProfile,
  getSocialSummary,
} from '../../../services/social/api';
import './PublicProfilePage.css';

const fallbackAvatar = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128">
  <defs><linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
    <stop stop-color="#0d4f3c"/><stop offset="1" stop-color="#d9ff31"/>
  </linearGradient></defs>
  <rect width="128" height="128" rx="64" fill="url(#g)"/>
  <circle cx="64" cy="48" r="23" fill="#f7efe6"/>
  <path d="M24 116c7-25 22-38 40-38s33 13 40 38" fill="#f7efe6"/>
</svg>`)}`;

const PublicProfilePage: React.FC = () => {
  const { userId = '' } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = React.useState<PublicProfile | null>(null);
  const [social, setSocial] = React.useState<SocialSummary | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([getPublicProfile(userId), getSocialSummary(userId)])
      .then(([publicProfile, summary]) => {
        if (cancelled) return;
        setProfile(publicProfile);
        setSocial(summary);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Could not load this profile.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Viewing your own public page → send to the editable profile.
  React.useEffect(() => {
    if (social?.isSelf) navigate('/profile', { replace: true });
  }, [social?.isSelf, navigate]);

  const displayName =
    [profile?.firstName, profile?.lastName].filter(Boolean).join(' ') ||
    profile?.nickname ||
    'Traveler';
  const handle = profile?.nickname ? `@${profile.nickname}` : '';

  const adjustFollowers = (following: boolean) =>
    setSocial((current) =>
      current ? { ...current, followers: Math.max(0, current.followers + (following ? 1 : -1)), isFollowing: following } : current,
    );

  return (
    <DashboardLayout activeSection="feed">
      <div className="v2-public-profile">
        {loading && <p className="v2-public-profile__state">Loading profile…</p>}

        {!loading && error && (
          <div className="v2-public-profile__state v2-public-profile__state--error">
            <p>{error}</p>
            <button type="button" onClick={() => navigate(-1)}>Go back</button>
          </div>
        )}

        {!loading && !error && !profile && (
          <div className="v2-public-profile__state v2-public-profile__state--error">
            <p>This user doesn’t exist yet.</p>
            <button type="button" onClick={() => navigate('/feed')}>Back to feed</button>
          </div>
        )}

        {!loading && !error && profile && (
          <section className="v2-public-profile__card">
            <img
              className="v2-public-profile__avatar"
              src={profile.pictureUrl || fallbackAvatar}
              alt=""
              onError={(event) => {
                event.currentTarget.src = fallbackAvatar;
              }}
            />
            <div className="v2-public-profile__identity">
              <h1>{displayName}</h1>
              {handle && <p className="v2-public-profile__handle">{handle}</p>}
              <div className="v2-public-profile__stats">
                <span><strong>{social?.followers ?? 0}</strong> Followers</span>
                <span><strong>{social?.following ?? 0}</strong> Following</span>
              </div>
            </div>
            {social && !social.isSelf && (
              <FollowButton
                userId={userId}
                initialFollowing={social.isFollowing}
                onChange={adjustFollowers}
              />
            )}
          </section>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PublicProfilePage;
